// Sprint 4 Quick-Win — Upsell-Toast für Free-User, der auf einen gelockten
// Steckbrief-Preview klickt. Trigger: URL-Query ?upsell=steckbrief-locked.
// Lokales localStorage-Memo verhindert wiederholtes Triggern (7-Tage-Sleep).

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { BRAND } from "@/config/brand";

const SNOOZE_KEY = "fts.upsell_toast.snoozed_until";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const FOKUSTYP_NAME = {
  eule: "Eule",
  biene: "Biene",
  katze: "Katze",
  hund: "Hund",
};

export function UpsellToast() {
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [fokustyp, setFokustyp] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const upsell = params.get("upsell");
    if (upsell !== "steckbrief-locked") return;

    // Snooze-Check: 7 Tage nach Dismiss nicht wieder zeigen.
    try {
      const snoozedUntil = parseInt(
        localStorage.getItem(SNOOZE_KEY) || "0",
        10,
      );
      if (snoozedUntil > Date.now()) {
        // Snooze aktiv — URL-Param trotzdem entfernen.
        const cleaned = new URLSearchParams(location.search);
        cleaned.delete("upsell");
        cleaned.delete("fokustyp");
        const qs = cleaned.toString();
        navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
        return;
      }
    } catch (e) {
      // localStorage nicht verfügbar — Toast trotzdem zeigen.
    }

    setFokustyp(params.get("fokustyp"));
    setVisible(true);
  }, [location.search, location.pathname, navigate]);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SEVEN_DAYS_MS));
    } catch (e) { /* noop */ }
    const cleaned = new URLSearchParams(location.search);
    cleaned.delete("upsell");
    cleaned.delete("fokustyp");
    const qs = cleaned.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
  };

  if (!visible) return null;

  const ftName = FOKUSTYP_NAME[fokustyp] || "Fokustyp";

  return (
    <div
      data-testid="upsell-toast"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white shadow-2xl rounded-xl p-4 max-w-md w-[92%] flex items-start gap-3 border-2 border-amber-300"
    >
      <span className="text-2xl select-none" aria-hidden="true">🔒</span>
      <div className="flex-1 min-w-0">
        <p
          data-testid="upsell-toast-headline"
          className="font-bold text-stone-900 text-sm"
        >
          Mehr zum {ftName}-Steckbrief
        </p>
        <p className="mt-1 text-xs text-stone-600 leading-snug">
          Mit <span className="font-semibold">{BRAND.name}</span> Pro Solo bekommst du alle 4 Steckbriefe.
        </p>
      </div>
      <a
        data-testid="upsell-toast-cta"
        href="/dashboard/abo"
        className="flex-shrink-0 bg-blue-700 hover:bg-blue-800 text-white px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors"
      >
        Pro Solo · 39 €/Monat
      </a>
      <button
        type="button"
        data-testid="upsell-toast-dismiss"
        onClick={dismiss}
        className="flex-shrink-0 text-stone-400 hover:text-stone-600 transition-colors"
        aria-label="Schließen"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default UpsellToast;
